/**
 * Industry-standard slug rules for URLs:
 * - Lowercase letters (a-z), digits (0-9), hyphens (-) only
 * - Spaces → hyphens; no leading/trailing hyphen; no consecutive hyphens
 * - Length 1–100
 */

const SLUG_MIN_LENGTH = 1;
const SLUG_MAX_LENGTH = 100;

/** Reserved slugs that cannot be used (e.g. for routing) */
const RESERVED_SLUGS = new Set([
  'admin', 'api', 'login', 'logout', 'register', 'auth', 'settings',
  'dashboard', 'tenants', 'static', 'assets', 'public', 'app',
]);

/**
 * Generate a URL slug from a display name.
 * @param {string} name - Display name (e.g. "My Club Name")
 * @returns {string} - Slug (e.g. "my-club-name")
 */
export function slugFromName(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Validate a slug and return an error message if invalid.
 * @param {string} slug - Slug to validate
 * @param {object} options - { reserved?: Set<string> }
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateSlug(slug, options = {}) {
  const reserved = options.reserved || RESERVED_SLUGS;
  if (slug === undefined || slug === null) {
    return { valid: false, error: 'Slug is required.' };
  }
  const s = String(slug).trim();
  if (s.length === 0) {
    return { valid: false, error: 'Slug cannot be empty.' };
  }
  if (s.length < SLUG_MIN_LENGTH) {
    return { valid: false, error: `Slug must be at least ${SLUG_MIN_LENGTH} character.` };
  }
  if (s.length > SLUG_MAX_LENGTH) {
    return { valid: false, error: `Slug must be at most ${SLUG_MAX_LENGTH} characters.` };
  }
  if (!/^[a-z0-9-]+$/.test(s)) {
    return { valid: false, error: 'Only lowercase letters (a–z), numbers (0–9), and hyphens (-) are allowed.' };
  }
  if (s.startsWith('-') || s.endsWith('-')) {
    return { valid: false, error: 'Slug cannot start or end with a hyphen.' };
  }
  if (/--/.test(s)) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens.' };
  }
  if (reserved.has(s)) {
    return { valid: false, error: `"${s}" is a reserved slug. Please choose another.` };
  }
  return { valid: true };
}

/**
 * Normalize user input toward a valid slug (for live editing).
 * @param {string} value - Raw input
 * @returns {string}
 */
export function normalizeSlugInput(value) {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, SLUG_MAX_LENGTH);
}
